import type { PurchaseResponse } from 'src/services/LeadStreamsService/agent';

export type PendingPurchase = { id: number; streamId: number; confirmationUrl: string };
export type PendingCheckout = {
    purchases: PendingPurchase[];
    expires: number;
    selectionKey?: string;
    selectionRevision?: string;
};
const key = (owner: number) => 'lm_package_checkout:' + owner;
const requestStorageKey = (owner: number, streamId: number) => `lm_package_request:${owner}:${streamId}`;

export function packageRequestKey(owner: number, streamId: number, selection: object): string {
    const fingerprint = JSON.stringify(selection);
    const storageKey = requestStorageKey(owner, streamId);
    try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
        if (saved?.fingerprint === fingerprint && typeof saved?.key === 'string') {
            if (saved.expires <= Date.now()) throw new Error('Срок проверки оплаты истёк. Обратитесь в поддержку.');
            return saved.key;
        }
        const requestKey = crypto.randomUUID();
        localStorage.setItem(storageKey, JSON.stringify({
            fingerprint, key: requestKey, expires: Date.now() + 23 * 60 * 60 * 1000,
        }));
        return requestKey;
    } catch (error) {
        if (error instanceof Error && error.message.includes('Срок проверки')) throw error;
        throw new Error('Не удалось сохранить операцию оплаты. Проверьте доступ к хранилищу браузера.');
    }
}

export function clearPackageRequestKey(owner: number, streamId: number) {
    localStorage.removeItem(requestStorageKey(owner, streamId));
}

export function paymentLink(value: unknown): string {
    if (typeof value !== 'string') return '';
    try {
        const url = new URL(value);
        if (url.protocol !== 'https:' || url.username || url.password) return '';
        if (!['yoomoney.ru', 'yookassa.ru'].some((host) => url.hostname === host || url.hostname.endsWith('.' + host)))
            return '';
        return url.href;
    } catch {
        return '';
    }
}

export function rememberPackageCheckout(
    owner: number,
    purchases: Array<{ streamId: number; response: PurchaseResponse }>,
    selectionKey?: string
) {
    if (!owner || !purchases.length) return;
    try {
        const selectionRevision = selectionKey
            ? JSON.parse(localStorage.getItem(selectionKey) || 'null')?.revision
            : undefined;
        const pending: PendingCheckout = {
            purchases: purchases.map(({ streamId, response }) => ({
                id: response.purchase_id,
                streamId,
                confirmationUrl: paymentLink(response.confirmation_url),
            })),
            expires: Date.now() + 24 * 60 * 60 * 1000,
            selectionKey,
            selectionRevision,
        };
        sessionStorage.setItem(key(owner), JSON.stringify(pending));
    } catch {
        /* The payment still works; the return screen will offer a manual check. */
    }
}

export function readPackageCheckout(owner: number): PendingCheckout | null {
    try {
        const value = JSON.parse(sessionStorage.getItem(key(owner)) || 'null');
        if (
            !value ||
            !Number.isFinite(value.expires) ||
            value.expires <= Date.now() ||
            !Array.isArray(value.purchases) ||
            !value.purchases.length ||
            value.purchases.length > 50
        )
            return null;
        if (
            !value.purchases.every(
                (p: PendingPurchase) =>
                    Number.isInteger(p.id) && p.id > 0 && Number.isInteger(p.streamId) && p.streamId > 0
            )
        )
            return null;
        return {
            ...value,
            purchases: value.purchases.map((p: PendingPurchase) => ({
                ...p,
                confirmationUrl: paymentLink(p.confirmationUrl),
            })),
        };
    } catch {
        return null;
    }
}

export function clearPackageCheckout(owner: number) {
    try {
        const pending = readPackageCheckout(owner);
        pending?.purchases.forEach(({ streamId }) => clearPackageRequestKey(owner, streamId));
        sessionStorage.removeItem(key(owner));
    } catch {
        /* Optional persistence. */
    }
}

export function clearConfirmedSelection(checkout: PendingCheckout) {
    if (!checkout.selectionKey?.startsWith('leadstream_selection') || !checkout.selectionRevision) return;
    try {
        const current = JSON.parse(localStorage.getItem(checkout.selectionKey) || 'null');
        if (current?.revision === checkout.selectionRevision) localStorage.removeItem(checkout.selectionKey);
    } catch {
        /* A newer or unavailable draft is preserved. */
    }
}
